import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import { money } from "../../lib/api";
import api from "../../lib/api";
import { Loading, EmptyState } from "../../components/States";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StageBar";
import { toast } from "sonner";
import { Plus, Target, FileText } from "lucide-react";

export default function Sales() {
  const leads = useFetch("/leads");
  const estimates = useFetch("/estimates");
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);

  const addLead = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/leads", { name, value: Number(value) || 0, source: "Manual" });
      toast.success("Lead added to pipeline");
      setName(""); setValue(""); setOpen(false);
      leads.refetch();
    } catch { toast.error("Could not add lead"); }
  };

  if (leads.loading || estimates.loading) return <Loading />;

  const bids = (estimates.data || []).filter((e) => ["sent", "pending"].includes(e.status));
  const pipelineValue = (leads.data || []).reduce((a, l) => a + (l.value || 0), 0);

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Sales Pipeline"
        title="Sales"
        subtitle={`${bids.length} bids need follow-up · ${money(pipelineValue)} in lead pipeline`}
        action={
          <button onClick={() => setOpen(!open)} className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-3 font-semibold text-slate-900 hover:bg-cyan-400" data-testid="add-lead-btn">
            <Plus size={18} /> New Lead
          </button>
        }
      />

      {open && (
        <form onSubmit={addLead} className="hw-card p-5 flex flex-col gap-3 sm:flex-row" data-testid="lead-form">
          <input className="hw-input" placeholder="Lead / company name" value={name} onChange={(e) => setName(e.target.value)} data-testid="lead-name" />
          <input className="hw-input sm:w-56" placeholder="Est. value ($)" value={value} onChange={(e) => setValue(e.target.value)} data-testid="lead-value" />
          <button className="rounded-xl bg-cyan-500 px-6 font-semibold text-slate-900 hover:bg-cyan-400" data-testid="lead-submit">Add</button>
        </form>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <h3 className="mb-4 flex items-center gap-2 font-head text-xl font-bold text-white"><FileText size={18} className="text-cyan-400" /> Outstanding Bids</h3>
          <div className="space-y-3">
            {bids.length === 0 && <EmptyState title="No open bids" subtitle="Everything has been won or closed." icon={FileText} />}
            {bids.map((b) => (
              <div key={b.id} className="hw-card p-5 hw-rise" data-testid={`bid-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">{b.title}</div>
                    <div className="mt-1 text-sm text-slate-400">{b.customer_name} · {b.type}</div>
                  </div>
                  <StatusPill status={b.status} />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-head text-2xl font-bold text-cyan-400">{money(b.total)}</span>
                  <span className="text-sm text-slate-500">{Math.round((b.margin || 0) * 100)}% margin</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-4 flex items-center gap-2 font-head text-xl font-bold text-white"><Target size={18} className="text-cyan-400" /> Lead Pipeline</h3>
          <div className="space-y-3">
            {(leads.data || []).map((l) => (
              <div key={l.id} className="hw-card p-5 hw-rise" data-testid={`lead-${l.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">{l.name}</div>
                    <div className="mt-1 text-sm text-slate-400 capitalize">{l.type} · {l.source}</div>
                    {l.notes && <div className="mt-2 text-sm text-slate-500">{l.notes}</div>}
                  </div>
                  <StatusPill status={l.status} />
                </div>
                <div className="mt-3 font-head text-xl font-bold text-emerald-300">{money(l.value)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
